// ────────────────────────────────────────────────────────────────────────────
//  Payroll.tsx — Lohnbuchhaltung & Zeitanalyse (Owner/Admin/can_view_salaries)
//
//  Drei Tabs:
//   1. Zeitanalyse — Stats + Pro-Mitarbeiter-Ranking
//   2. Lohnabrechnung — Stunden × Stundensatz, CSV-Export
//   3. Mitarbeiter — Stammdaten editieren (Position, Stundensatz, etc.)
// ────────────────────────────────────────────────────────────────────────────

import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useT, useLangCode } from '../i18n'
import { useExtensions } from '../lib/useExtensions'
import { useOrg } from '../lib/useOrg'
import {
  useTeamStats, useEmployees, updateEmployeeProfile,
  formatHours, calcGross,
  type TeamStatsMember, type Employee,
} from '../lib/usePayroll'
import { useTeamArchives, fmtDuration, getMemberRecords, recordSeconds, type PunchArchive, type PunchRecord } from '../lib/usePunch'
import { ArchiveDetailModal } from './Punch'
import { API_ORIGIN } from '../lib/api'

type Tab    = 'analysis' | 'payroll' | 'employees' | 'archives'
type Period = 'week' | 'month' | 'quarter' | 'year'

export function Payroll() {
  const { hasBusinessTier, loading: extLoading } = useExtensions()
  const { org, loading: orgLoading } = useOrg()

  if (extLoading || orgLoading) return <Spinner />
  if (!org) return <NoOrgRedirect />

  // Lohnbuchhaltung ist KEINE eigene Extension — sie ist in jedem
  // Business-Paket (Basic/L, monatlich/jährlich) automatisch enthalten,
  // genauso wie die automatische Org-Erstellung.
  if (!hasBusinessTier()) return <Locked />

  return <Panel />
}

function NoOrgRedirect() {
  const t = useT()
  return (
    <div className="max-w-xl mx-auto px-6 py-20 text-center">
      <div className="glass-strong p-8 md:p-10">
        <div className="text-6xl mb-4">🏢</div>
        <h1 className="text-2xl font-bold mb-2">{t('recruit.noOrgTitle')}</h1>
        <p className="text-app-text-dim leading-relaxed mb-6">{t('recruit.noOrgSub')}</p>
        <Link to="/app/org" className="btn-primary">{t('recruit.noOrgGoOrg')}</Link>
      </div>
    </div>
  )
}

function Locked() {
  const t = useT()
  return (
    <div className="max-w-xl mx-auto px-6 py-20 text-center">
      <div className="glass-strong p-8 md:p-10">
        <div className="text-6xl mb-4">🔒</div>
        <h1 className="text-2xl font-bold mb-2">{t('payroll.locked')}</h1>
        <p className="text-app-text-dim leading-relaxed mb-6">{t('payroll.lockedSub')}</p>
        <Link to="/app/shop" className="btn-primary">🛒 {t('payroll.lockedShop')}</Link>
      </div>
    </div>
  )
}

function Panel() {
  const t = useT()
  const [tab, setTab] = useState<Tab>('analysis')
  const [period, setPeriod] = useState<Period>('month')

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-4 md:px-6 py-5 sm:py-7 md:py-10 space-y-4 sm:space-y-5">
      <header>
        <h1 className="text-3xl md:text-4xl font-bold">{t('payroll.title')}</h1>
        <p className="text-sm text-app-text-dim mt-1.5">{t('payroll.sub')}</p>
      </header>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <nav>
          <div className="inline-flex gap-1 p-1 rounded-xl"
               style={{ background: 'var(--bg-input)', border: '1px solid var(--border)' }}>
            {([
              { id: 'analysis' as const,  l: t('payroll.tabAnalysis') },
              { id: 'payroll' as const,   l: t('payroll.tabPayroll') },
              { id: 'employees' as const, l: t('payroll.tabEmployees') },
              { id: 'archives' as const,  l: t('punch.archivesTitle') },
            ]).map(o => (
              <button key={o.id} onClick={() => setTab(o.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition whitespace-nowrap ${
                  tab === o.id ? 'bg-accent text-white shadow-md shadow-accent/30' : 'text-app-text-dim hover:text-app-text'
                }`}>
                {o.l}
              </button>
            ))}
          </div>
        </nav>

        {tab !== 'employees' && tab !== 'archives' && (
          <PeriodToggle period={period} onChange={setPeriod} />
        )}
      </div>

      {tab === 'analysis'  && <AnalysisTab period={period} />}
      {tab === 'payroll'   && <PayrollTab  period={period} />}
      {tab === 'employees' && <EmployeesTab />}
      {tab === 'archives'  && <ArchivesTab />}
    </div>
  )
}

// ── Tab: Archive (alle org-bezogenen, auch von ehemaligen Mitgliedern) ─────
function ArchivesTab() {
  const t = useT()
  const lang = useLangCode()
  const { items, loading, forbidden } = useTeamArchives()
  const [openId, setOpenId] = useState<number | null>(null)

  if (loading) return <Spinner small />
  if (forbidden) return <Forbidden />
  if (items.length === 0) {
    return (
      <section className="glass p-8 text-center">
        <div className="text-4xl mb-2 opacity-40">🗂️</div>
        <div className="text-sm text-app-text-mute">{t('punch.archivesEmpty')}</div>
      </section>
    )
  }

  return (
    <section className="glass p-5 md:p-7">
      <ul className="space-y-2">
        {items.map(a => <TeamArchiveRow key={a.id} a={a} t={t} lang={lang} onOpen={() => setOpenId(a.id)} />)}
      </ul>
      {openId !== null && (
        <ArchiveDetailModal id={openId} mode="team" onClose={() => setOpenId(null)} />
      )}
    </section>
  )
}

function TeamArchiveRow({ a, t, lang, onOpen }: { a: PunchArchive; t: any; lang: 'de' | 'en'; onOpen: () => void }) {
  const { h, m } = fmtDuration(a.totalSeconds)
  const date = new Date(a.createdAt).toLocaleDateString(lang, { day: '2-digit', month: 'long', year: 'numeric' })
  const initials = (a.userDisplayName ?? '?').split(/\s+/).map(s => s[0]).filter(Boolean).slice(0,2).join('').toUpperCase()
  return (
    <li>
      <button onClick={onOpen} className="w-full text-left rounded-xl glass-flat hover:bg-app-elev-2 transition p-4 flex flex-wrap items-center gap-3">
        <div className="grid place-items-center w-11 h-11 rounded-full text-xs font-bold text-white bg-gradient-to-br from-brandBlue to-accent shrink-0">
          {initials || '?'}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold truncate">{a.userDisplayName ?? '–'}</div>
          <div className="text-xs text-app-text-mute mt-0.5">
            {t(`punch.archiveReason.${a.reason}`)} · {t('punch.archiveMeta', { date })}
          </div>
        </div>
        <div className="text-sm text-right shrink-0">
          <div className="font-mono font-bold tabular-nums">{t('punch.durationFmt', { h, m })}</div>
          <div className="text-[10px] text-app-text-mute">{t('punch.archiveEntries', { n: a.totalEntries })}</div>
        </div>
      </button>
    </li>
  )
}

function PeriodToggle({ period, onChange }: { period: Period; onChange: (p: Period) => void }) {
  const t = useT()
  return (
    <div className="grid grid-cols-4 gap-1 p-1 rounded-xl"
         style={{ background: 'var(--bg-input)', border: '1px solid var(--border)' }}>
      {([
        { v: 'week' as const,    l: t('payroll.rangeWeek') },
        { v: 'month' as const,   l: t('payroll.rangeMonth') },
        { v: 'quarter' as const, l: t('payroll.rangeQuarter') },
        { v: 'year' as const,    l: t('payroll.rangeYear') },
      ]).map(o => (
        <button key={o.v} onClick={() => onChange(o.v)}
          className={`px-3 py-1.5 rounded-lg text-xs font-medium transition whitespace-nowrap ${
            period === o.v ? 'bg-accent text-white' : 'text-app-text-dim hover:text-app-text'
          }`}>
          {o.l}
        </button>
      ))}
    </div>
  )
}

// ── Tab: Zeitanalyse ───────────────────────────────────────────────────────
function AnalysisTab({ period }: { period: Period }) {
  const t = useT()
  const { stats, loading, forbidden } = useTeamStats(period)
  if (loading) return <Spinner small />
  if (forbidden) return <Forbidden />

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
        <StatTile icon="⏱" label={t('payroll.statTotal')} value={fmtHours(stats?.orgTotalSeconds ?? 0)} unit="h" />
        <StatTile icon="👥" label={t('payroll.statEmployees')} value={String(stats?.employees ?? 0)} />
        <StatTile icon="∑" label={t('payroll.statAvgPerEmp')} value={fmtHours(stats?.orgAverageSecondsPerEmployee ?? 0)} unit="h" />
        <StatTile icon="📅" label={t('payroll.statWorkDays')} value={String(stats?.orgWorkDays ?? 0)} />
      </div>

      <PerMemberRanking members={stats?.perMember ?? []} />
    </div>
  )
}

function StatTile({ icon, label, value, unit }: { icon: string; label: string; value: string; unit?: string }) {
  return (
    <div className="glass p-4 md:p-5">
      <div className="text-2xl">{icon}</div>
      <div className="mt-2 text-2xl md:text-3xl font-bold tabular-nums">
        {value}{unit && <span className="text-base text-app-text-mute font-medium ml-1">{unit}</span>}
      </div>
      <div className="text-[11px] text-app-text-mute font-medium tracking-wide uppercase mt-0.5">{label}</div>
    </div>
  )
}

function PerMemberRanking({ members }: { members: TeamStatsMember[] }) {
  const t = useT()
  const [openMember, setOpenMember] = useState<TeamStatsMember | null>(null)
  if (!members.length) return null
  const sorted = [...members].sort((a, b) => b.totalSeconds - a.totalSeconds)
  const max = Math.max(...sorted.map(m => m.totalSeconds), 1)

  return (
    <section className="glass p-5 md:p-7">
      <header className="mb-4">
        <h3 className="font-semibold flex items-center gap-2"><span className="text-lg">📊</span>{t('payroll.perMemberTitle')}</h3>
      </header>
      <ul className="space-y-2">
        {sorted.map(m => {
          const pct = (m.totalSeconds / max) * 100
          return (
            <li key={m.userId}>
              <button
                onClick={() => setOpenMember(m)}
                className="w-full text-left rounded-xl glass-flat hover:bg-app-elev-2 p-3 transition"
                title={t('payroll.memberOpenRecords')}
              >
                <div className="flex items-center gap-3 mb-2">
                  <Avatar member={m} />
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate flex items-center gap-2">
                      {m.displayName}
                      {m.isClockedInNow && (
                        <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-teal">
                          <span className="w-1.5 h-1.5 rounded-full bg-teal animate-pulse" />{t('payroll.statusLive')}
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-app-text-mute">{m.daysWorked} {t('payroll.colDays')} · {Math.round(m.pauseSeconds / 60)}min {t('payroll.colPause')}</div>
                  </div>
                  <div className="font-bold tabular-nums">{fmtHours(m.totalSeconds)} {t('payroll.colHours')}</div>
                  <span className="text-app-text-mute text-lg">›</span>
                </div>
                <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--bg-input)' }}>
                  <div className="h-full rounded-full bg-gradient-to-r from-accent to-brandBlue" style={{ width: `${pct}%` }} />
                </div>
              </button>
            </li>
          )
        })}
      </ul>
      {openMember && (
        <MemberRecordsSheet member={openMember} onClose={() => setOpenMember(null)} />
      )}
    </section>
  )
}

// ── Member-Detail-Sheet: Records des Mitarbeiters mit Edit-Möglichkeit ─────
function MemberRecordsSheet({ member, onClose }: { member: TeamStatsMember; onClose: () => void }) {
  const t = useT()
  const lang = useLangCode()
  const [records, setRecords] = useState<PunchRecord[] | null>(null)
  const [editing, setEditing] = useState<PunchRecord | null>(null)

  const load = async () => {
    try {
      const today = new Date()
      const from = new Date(today); from.setDate(from.getDate() - 30)
      const isoDay = (d: Date) => d.toISOString().slice(0, 10)
      const r = await getMemberRecords(member.userId, { from: isoDay(from) })
      setRecords(r)
    } catch (e: any) {
      alert(e?.message ?? 'failed')
      setRecords([])
    }
  }
  useEffect(() => { void load() }, [member.userId])

  return (
    <div className="fixed inset-0 z-50 grid place-items-start sm:place-items-center p-0 sm:p-4 backdrop-blur-2xl"
         style={{ background: 'rgba(0,0,0,0.72)' }} onClick={onClose}>
      <div className="w-full max-w-3xl glass-strong p-5 md:p-7 sm:rounded-3xl rounded-none max-h-screen overflow-y-auto"
           onClick={e => e.stopPropagation()}>
        <header className="mb-4 flex items-start justify-between gap-3">
          <div>
            <h2 className="text-xl md:text-2xl font-bold">{t('payroll.memberDetailTitle', { name: member.displayName })}</h2>
            <p className="text-xs text-app-text-mute mt-0.5">{t('payroll.memberDetailSub')}</p>
          </div>
          <button onClick={onClose} className="text-app-text-mute hover:text-app-text text-2xl">×</button>
        </header>

        {records === null ? (
          <div className="text-center py-12 text-app-text-mute">…</div>
        ) : records.length === 0 ? (
          <div className="text-center py-8 text-sm text-app-text-mute">{t('payroll.memberRecordsEmpty')}</div>
        ) : (
          <ul className="space-y-2">
            {records.map(r => {
              const sec = recordSeconds(r)
              const { h, m } = fmtDuration(sec)
              const date = new Date(r.clockIn).toLocaleDateString(lang, { day: '2-digit', month: '2-digit', year: '2-digit' })
              const time = new Date(r.clockIn).toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' }) +
                (r.clockOut ? ` – ${new Date(r.clockOut).toLocaleTimeString(lang, { hour: '2-digit', minute: '2-digit' })}` : ' – …')
              return (
                <li key={r.id}>
                  <button onClick={() => setEditing(r)} className="w-full text-left rounded-xl glass-flat hover:bg-app-elev-2 transition p-3 flex flex-wrap items-center gap-3">
                    <div className="text-[11px] font-bold tracking-wider uppercase text-app-text-mute w-16 shrink-0">{date}</div>
                    <div className="font-mono text-sm">{time}</div>
                    <div className="flex-1" />
                    {r.isManual && (
                      <span className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-full"
                            style={{ background: 'rgba(255,179,0,0.15)', color: '#ffb300', border: '1px solid rgba(255,179,0,0.30)' }}>
                        {t('punch.editManualBadge')}
                      </span>
                    )}
                    <div className="font-bold tabular-nums">{r.clockOut ? t('punch.durationFmt', { h, m }) : '…'}</div>
                    <span className="text-app-text-mute">›</span>
                  </button>
                  {r.note && <div className="ml-16 mt-1 text-xs text-app-text-dim">{r.note}</div>}
                </li>
              )
            })}
          </ul>
        )}

        {editing && (
          <MemberEditModal record={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load() }} />
        )}
      </div>
    </div>
  )
}

// Wir delegieren an EditRecordModal aus Punch.tsx — aber das ist dort
// `function`-scoped. Stattdessen nutzen wir den exportierten Helper:
import { EditRecordModalExternal } from './Punch'
function MemberEditModal({ record, onClose, onSaved }: { record: PunchRecord; onClose: () => void; onSaved: () => void }) {
  return <EditRecordModalExternal record={record} onClose={onClose} onSaved={onSaved} />
}

// ── Tab: Lohnabrechnung ─────────────────────────────────────────────────────
function PayrollTab({ period }: { period: Period }) {
  const t = useT()
  const lang = useLangCode()
  const { stats, loading: sLoading, forbidden } = useTeamStats(period)
  const { items: employees, loading: eLoading } = useEmployees()

  if (sLoading || eLoading) return <Spinner small />
  if (forbidden) return <Forbidden />

  const empByUserId = new Map(employees.map(e => [e.userId, e]))

  const rows = (stats?.perMember ?? []).map(m => {
    const e = empByUserId.get(m.userId)
    const rate = e?.hourlyRate ?? null
    const gross = calcGross(m.totalSeconds, rate)
    return { m, e, rate, gross }
  }).sort((a, b) => (b.gross ?? 0) - (a.gross ?? 0))

  const orgGross = rows.reduce((s, r) => s + (r.gross ?? 0), 0)

  const exportCsv = () => {
    const header = ['Name', 'Email', 'Tage', 'Stunden', 'Pause (Std)', 'Stundensatz', 'Brutto']
    const data = rows.map(r => [
      r.m.displayName,
      r.m.email ?? '',
      r.m.daysWorked,
      formatHours(r.m.totalSeconds),
      formatHours(r.m.pauseSeconds),
      r.rate != null ? r.rate.toFixed(2).replace('.', ',') : '',
      r.gross != null ? r.gross.toFixed(2).replace('.', ',') : '',
    ])
    const csv = [header, ...data].map(row => row.map(c => `"${String(c).replace(/"/g, '""')}"`).join(';')).join('\n')
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = t('payroll.csvFile', { period })
    a.click()
    URL.revokeObjectURL(url)
  }

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
        <StatTile icon="💰" label={`${t('payroll.colGross')} ${period}`} value={fmtMoney(orgGross, lang)} />
        <StatTile icon="⏱" label={t('payroll.statTotal')} value={fmtHours(stats?.orgTotalSeconds ?? 0)} unit="h" />
        <StatTile icon="👥" label={t('payroll.statEmployees')} value={String(rows.length)} />
      </div>

      <section className="glass p-0 md:p-2 overflow-hidden">
        <header className="p-5 md:px-5 md:py-4 flex items-center justify-between gap-3">
          <div>
            <h3 className="font-semibold flex items-center gap-2"><span className="text-lg">💰</span>{t('payroll.tabPayroll')}</h3>
            <p className="text-xs text-app-text-mute mt-0.5">{t('payroll.payrollHint')}</p>
          </div>
          <button onClick={exportCsv} className="btn-ghost text-sm py-2 px-4 shrink-0">📥 {t('payroll.csvExport')}</button>
        </header>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[10px] font-bold tracking-wider uppercase text-app-text-mute">
              <tr style={{ borderTop: '1px solid var(--border)' }}>
                <th className="text-left px-4 md:px-5 py-3">{t('payroll.colName')}</th>
                <th className="text-right px-2 py-3">{t('payroll.colDays')}</th>
                <th className="text-right px-2 py-3">{t('payroll.colWorked')}</th>
                <th className="text-right px-2 py-3 hidden sm:table-cell">{t('payroll.colHourly')}</th>
                <th className="text-right px-4 md:px-5 py-3">{t('payroll.colGross')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-10 text-app-text-mute">{t('payroll.perMemberEmpty')}</td></tr>
              ) : rows.map(r => (
                <tr key={r.m.userId} style={{ borderTop: '1px solid var(--border)' }}>
                  <td className="px-4 md:px-5 py-3">
                    <div className="flex items-center gap-2.5">
                      <Avatar member={r.m} small />
                      <div className="min-w-0">
                        <div className="font-medium truncate">{r.m.displayName}</div>
                        <div className="text-[10px] text-app-text-mute truncate">{r.m.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="text-right px-2 py-3 tabular-nums">{r.m.daysWorked}</td>
                  <td className="text-right px-2 py-3 tabular-nums font-mono">{fmtHours(r.m.totalSeconds)}</td>
                  <td className="text-right px-2 py-3 tabular-nums hidden sm:table-cell">{r.rate != null ? fmtMoney(r.rate, lang) : '—'}</td>
                  <td className="text-right px-4 md:px-5 py-3 tabular-nums font-bold">{r.gross != null ? fmtMoney(r.gross, lang) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  )
}

// ── Tab: Mitarbeiter / Profile editieren ───────────────────────────────────
function EmployeesTab() {
  const t = useT()
  const lang = useLangCode()
  const { items, loading, refresh } = useEmployees()
  const [editing, setEditing] = useState<Employee | null>(null)

  if (loading) return <Spinner small />

  if (items.length === 0) {
    return (
      <section className="glass p-8 text-center">
        <div className="text-4xl mb-2 opacity-40">👥</div>
        <div className="text-sm text-app-text-mute">{t('payroll.employeesEmpty')}</div>
      </section>
    )
  }

  return (
    <div className="space-y-3">
      {items.map(e => (
        <EmployeeCard key={e.userId} e={e} t={t} lang={lang} onEdit={() => setEditing(e)} />
      ))}
      <div className="text-xs text-app-text-mute pt-2">{t('payroll.empSalaryHidden')}</div>
      {editing && <EmployeeEditModal e={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh() }} />}
    </div>
  )
}

function EmployeeCard({ e, lang, onEdit }: { e: Employee; t: any; lang: string; onEdit: () => void }) {
  const initials = e.displayName.split(/\s+/).map(s => s[0]).filter(Boolean).slice(0,2).join('').toUpperCase()
  return (
    <section className="glass p-4 md:p-5">
      <div className="flex flex-wrap items-center gap-4">
        <div className="w-12 h-12 rounded-full grid place-items-center text-sm font-bold text-white bg-gradient-to-br from-accent to-accent2 shrink-0">
          {initials || '?'}
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold truncate">{e.displayName}</div>
          <div className="text-xs text-app-text-mute truncate flex flex-wrap gap-x-3 gap-y-1 mt-0.5">
            <span>{e.email}</span>
            {e.positionTitle && <span>· {e.positionTitle}</span>}
            {e.departmentName && <span>· {e.departmentName}</span>}
          </div>
        </div>
        <div className="flex items-center gap-3 text-sm">
          {e.salaryVisibleToCaller && e.hourlyRate != null && (
            <span className="font-mono tabular-nums">{fmtMoney(e.hourlyRate, lang)}/h</span>
          )}
          {e.weeklyHours != null && (
            <span className="text-xs text-app-text-mute">{e.weeklyHours}h/{lang === 'de' ? 'W' : 'wk'}</span>
          )}
          <button onClick={onEdit} className="btn-ghost text-xs py-1.5 px-3">📝</button>
        </div>
      </div>
    </section>
  )
}

function EmployeeEditModal({ e, onClose, onSaved }: { e: Employee; onClose: () => void; onSaved: () => void }) {
  const t = useT()
  const [hourlyRate, setHourlyRate] = useState(e.hourlyRate?.toString() ?? '')
  const [monthlySalary, setMonthlySalary] = useState(e.monthlySalary?.toString() ?? '')
  const [weeklyHours, setWeeklyHours] = useState(e.weeklyHours?.toString() ?? '')
  const [hireDate, setHireDate] = useState(e.hireDate ?? '')
  const [notes, setNotes] = useState(e.profileNotes ?? '')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [ok, setOk]   = useState(false)

  const submit = async (ev: React.FormEvent) => {
    ev.preventDefault(); setBusy(true); setErr(null); setOk(false)
    try {
      await updateEmployeeProfile(e.userId, {
        hourlyRate:    hourlyRate ? parseFloat(hourlyRate.replace(',', '.')) : null,
        monthlySalary: monthlySalary ? parseFloat(monthlySalary.replace(',', '.')) : null,
        weeklyHours:   weeklyHours ? parseFloat(weeklyHours.replace(',', '.')) : null,
        hireDate:      hireDate || null,
        profileNotes:  notes || null,
      })
      setOk(true); setTimeout(() => onSaved(), 800)
    } catch (e: any) { setErr(e?.message ?? 'failed') }
    finally { setBusy(false) }
  }

  return (
    <div className="fixed inset-0 z-50 grid place-items-start sm:place-items-center p-4 backdrop-blur-2xl"
         style={{ background: 'rgba(0,0,0,0.72)' }} onClick={onClose}>
      <div className="w-full max-w-lg glass-strong p-5 md:p-6 mt-4 sm:mt-0" onClick={ev => ev.stopPropagation()}>
        <header className="mb-4 flex items-start justify-between">
          <div>
            <h3 className="font-bold text-lg">{e.displayName}</h3>
            <div className="text-xs text-app-text-mute">{t('payroll.empSetSalary')}</div>
          </div>
          <button onClick={onClose} className="text-app-text-mute hover:text-app-text text-2xl">×</button>
        </header>
        <form onSubmit={submit} className="space-y-3">
          <div className="grid sm:grid-cols-2 gap-3">
            <FormField label={t('payroll.colHourly')}>
              <input type="text" inputMode="decimal" value={hourlyRate} onChange={ev => setHourlyRate(ev.target.value)} placeholder="15,50" className="input-base" />
            </FormField>
            <FormField label={t('payroll.empWeeklyHours')}>
              <input type="text" inputMode="decimal" value={weeklyHours} onChange={ev => setWeeklyHours(ev.target.value)} placeholder="40" className="input-base" />
            </FormField>
            <FormField label={t('shop.productName.PunchClock')}>
              <input type="text" inputMode="decimal" value={monthlySalary} onChange={ev => setMonthlySalary(ev.target.value)} placeholder="3000" className="input-base" />
            </FormField>
            <FormField label={t('payroll.empHireDate')}>
              <input type="date" value={hireDate} onChange={ev => setHireDate(ev.target.value)} className="input-base" />
            </FormField>
          </div>
          <FormField label={t('payroll.empNotes')}>
            <textarea value={notes} onChange={ev => setNotes(ev.target.value)} rows={3} className="input-base" />
          </FormField>
          {err && <div className="text-sm text-coral bg-coral/10 border border-coral/30 rounded-lg p-2.5">{err}</div>}
          {ok && <div className="text-sm text-teal">{t('payroll.empSaved')}</div>}
          <div className="flex gap-2 justify-end">
            <button type="button" onClick={onClose} className="btn-ghost text-sm py-2 px-4">{t('common.cancel')}</button>
            <button type="submit" disabled={busy} className="btn-primary text-sm py-2 px-4 disabled:opacity-50">
              {busy ? '…' : t('payroll.empSave')}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Helpers ─────────────────────────────────────────────────────────────────
function Forbidden() {
  const t = useT()
  return (
    <section className="glass p-8 text-center">
      <div className="text-5xl mb-3 opacity-50">🚫</div>
      <h3 className="font-semibold text-lg">{t('payroll.forbidden')}</h3>
      <p className="text-sm text-app-text-mute mt-1 max-w-sm mx-auto">{t('payroll.forbiddenSub')}</p>
    </section>
  )
}

function Avatar({ member, small }: { member: { displayName: string; avatarUrl?: string | null }; small?: boolean }) {
  const initials = member.displayName.split(/\s+/).map(s => s[0]).filter(Boolean).slice(0,2).join('').toUpperCase()
  const sz = small ? 'w-8 h-8 text-[10px]' : 'w-10 h-10 text-xs'
  if (member.avatarUrl) {
    const src = member.avatarUrl.startsWith('http') ? member.avatarUrl : `${API_ORIGIN}${member.avatarUrl}`
    return <img src={src} alt="" className={`${sz} rounded-full object-cover shrink-0`} />
  }
  return (
    <div className={`${sz} rounded-full grid place-items-center font-bold text-white bg-gradient-to-br from-brandBlue to-accent shrink-0`}>
      {initials || '?'}
    </div>
  )
}

function FormField(props: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-bold text-app-text-dim mb-1.5 tracking-wider uppercase">{props.label}</span>
      {props.children}
    </label>
  )
}

function Spinner({ small }: { small?: boolean }) {
  const sz = small ? 'w-6 h-6 border-2' : 'w-10 h-10 border-4'
  return (
    <div className="flex items-center justify-center py-12">
      <div className={`${sz} rounded-full border-app-border border-t-accent animate-spin`} />
    </div>
  )
}

function fmtHours(seconds: number): string {
  const h = seconds / 3600
  return h.toFixed(1).replace('.', ',')
}

function fmtMoney(value: number, lang: string): string {
  try {
    return new Intl.NumberFormat(lang, { style: 'currency', currency: 'EUR' }).format(value)
  } catch {
    return `${value.toFixed(2).replace('.', ',')} €`
  }
}
